import React, { useState, useRef, useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { isValidYouTubeUrl, getYouTubeThumbnail, getYouTubeEmbedUrl } from '../../utils/youtubeHelper';

const SWIPE_THRESHOLD = 50;

/**
 * Image/video carousel for event cards in the Home list.
 * Mirrors ReviewMediaCarousel styling (arrows, dots, swipe)
 * and supports plain image URLs and YouTube video URLs.
 * Arrows and touch swipe are always enabled regardless of viewport width,
 * since the list panel is often narrower than the 'sm' breakpoint.
 */
const EventMediaCarousel = ({ images = [], altText = '', height = 180 }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [playingVideoIdx, setPlayingVideoIdx] = useState(null);
  const touchStartX = useRef(null);

  const count = images.length;

  const goTo = useCallback((idx) => {
    const newIdx = Math.max(0, Math.min(idx, count - 1));
    setActiveIndex(newIdx);
    if (playingVideoIdx !== null && playingVideoIdx !== newIdx) setPlayingVideoIdx(null);
  }, [count, playingVideoIdx]);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current == null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      goTo(activeIndex + (diff > 0 ? 1 : -1));
    }
    touchStartX.current = null;
  }, [activeIndex, goTo]);

  const handlePlayVideo = useCallback((idx) => {
    if (isValidYouTubeUrl(images[idx])) {
      setPlayingVideoIdx(idx);
    } else {
      window.open(images[idx], '_blank', 'noopener');
    }
  }, [images]);

  if (count === 0) return null;

  // Single item — no carousel UI
  if (count === 1) {
    const src = images[0];
    const isYT = isValidYouTubeUrl(src);
    const isPlaying = playingVideoIdx === 0;

    if (isYT && isPlaying) {
      return (
        <Box
          component="iframe"
          src={`${getYouTubeEmbedUrl(src)}?autoplay=1`}
          allow="autoplay; encrypted-media"
          allowFullScreen
          frameBorder="0"
          sx={{
            width: '100%',
            height,
            border: 'none',
            display: 'block',
            borderTopLeftRadius: 3,
            borderTopRightRadius: 3,
          }}
        />
      );
    }

    const imgSrc = isYT ? getYouTubeThumbnail(src, 'hq') : src;
    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height,
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3,
          overflow: 'hidden',
          cursor: isYT ? 'pointer' : 'default',
        }}
        onClick={isYT ? () => handlePlayVideo(0) : undefined}
      >
        <Box
          component="img"
          src={imgSrc}
          alt={altText}
          loading="eager"
          sx={{ width: '100%', height, objectFit: 'cover', display: 'block' }}
        />
        {isYT && (
          <PlayCircleOutlineIcon
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: 56,
              color: 'rgba(255,255,255,0.9)',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>
    );
  }

  // Multi-item carousel
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height,
        overflow: 'hidden',
        borderTopLeftRadius: 3,
        borderTopRightRadius: 3,
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slides container */}
      <Box
        sx={{
          display: 'flex',
          width: `${count * 100}%`,
          height: '100%',
          transform: `translateX(-${activeIndex * (100 / count)}%)`,
          transition: 'transform 0.3s ease',
        }}
      >
        {images.map((src, idx) => {
          const isYT = isValidYouTubeUrl(src);
          const isPlaying = playingVideoIdx === idx;
          const imgSrc = isYT ? getYouTubeThumbnail(src, 'hq') : src;

          return (
            <Box
              key={idx}
              sx={{
                width: `${100 / count}%`,
                height: '100%',
                flexShrink: 0,
                position: 'relative',
                cursor: isYT && !isPlaying ? 'pointer' : 'default',
              }}
              onClick={isYT && !isPlaying ? () => handlePlayVideo(idx) : undefined}
            >
              {isPlaying ? (
                <Box
                  component="iframe"
                  src={`${getYouTubeEmbedUrl(src)}?autoplay=1`}
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  frameBorder="0"
                  sx={{ width: '100%', height: '100%', border: 'none' }}
                />
              ) : (
                <>
                  <Box
                    component="img"
                    src={imgSrc}
                    alt={`${altText} ${idx + 1}`}
                    loading={idx === 0 ? 'eager' : 'lazy'}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  {isYT && (
                    <PlayCircleOutlineIcon
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontSize: 56,
                        color: 'rgba(255,255,255,0.9)',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                </>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Arrow buttons — always visible when multiple items */}
      <>
        {activeIndex > 0 && (
          <IconButton
            size="small"
            onClick={() => goTo(activeIndex - 1)}
            sx={{
              position: 'absolute',
              top: '50%',
              left: 4,
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0,0,0,0.45)',
              color: '#fff',
              zIndex: 2,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
              width: 30,
              height: 30,
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
        {activeIndex < count - 1 && (
          <IconButton
            size="small"
            onClick={() => goTo(activeIndex + 1)}
            sx={{
              position: 'absolute',
              top: '50%',
              right: 4,
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0,0,0,0.45)',
              color: '#fff',
              zIndex: 2,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
              width: 30,
              height: 30,
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        )}
      </>

      {/* Dot indicators */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 6,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 0.6,
        }}
      >
        {images.map((_, idx) => (
          <Box
            key={idx}
            onClick={() => goTo(idx)}
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: idx === activeIndex ? '#fff' : 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              boxShadow: '0 1px 2px rgba(0,0,0,0.4)',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default React.memo(EventMediaCarousel);
